import { Avatar } from "@mui/material";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TableLoader from "@/Components/common/TableLoader";
import TableNoContent from "@/Components/common/TableNoContent";
import { routingConstants } from "@/Constants/routingConstants";
import {
  fetchInvitedCandidates,
  updateSelectedCandidate,
  updateToggleSelected,
  updatePagination,
  deSelectAllCandidate,
  toggleSelectedAll,
  setInvitedCandidatesLoading,
  updateSorting,
} from "@/reducers/client_candidates/candidatesinvited";
import {
  getActionByHtml,
  getColumnNormalHtml,
} from "@/utilities/datatable/columnFunctions";
import { sortByDate, sortByText } from "@/utilities/DataTableSorting";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";
import { getComplianceClass } from "@/utilities/utils";

import { Miles } from "./common/Miles";
import { SelectedCountText } from "./common/SelectedCountText";
import { Actions } from "./Invited/Actions";
import BulkInvite from "./Invited/BulkInvite";
import { FilterModal } from "./Invited/FilterModal.jsx";
import { SearchFilter } from "./Invited/SearchFilter";
import DataTablePaginationLoader from "../../../../Skeletons/DataTablePaginationLoader.jsx";
import LiveTimerTooltip from "../../contracts/Unfilled/LiveTimerTooltip.jsx";
import CandidateStatusAndInfo from "../../dataTables/CandidateStatusAndInfo";
import BlurredText from "../../managedService/BlurredText";

/**
 * Function component for displaying invited candidates list and handling related actions.
 *
 * @return {JSX.Element} The JSX for displaying invited candidates list and related actions.
 */
export const InvitedList = () => {
  const dispatch = useDispatch();
  const {
    invitedCandidates,
    selectedCandidate,
    isLoading,
    toggleSelected,
    totalCount,
    pagination,
    isSelectedAll,
    isPaginationLoading,
    sorting,
  } = useSelector((state) => state.clientCandidatesInvited);
  const { isPremiumAccount, clientUserAllPermissions } = useSelector(
    (state) => state.session,
  );
  const [data, setData] = useState([]);
  const { user } = useSelector((state) => state.clientProfile);

  useEffect(() => {
    setData(invitedCandidates);
  }, [invitedCandidates]);
  useEffect(() => {
    if (selectedCandidate.length == 0) {
      dispatch(updateToggleSelected());
    }
  }, [selectedCandidate]);

  const columns = [
    {
      name: "Candidate",
      sortable: true,
      sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_name"),
      sortField: "candidate_name",
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name
                .trim()
                .split(" ")[0]
                .charAt(0)
                .toUpperCase()}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {row.route_id == "-" ? (
              <span className="truncate text-[14px] font-semibold capitalize leading-[normal] text-black">
                {row.candidate_name}
              </span>
            ) : !clientUserAllPermissions ||
              clientUserAllPermissions.is_allow_to_view_candidate_profile ===
                1 ? (
              <>
                <Link
                  to={routingConstants.candidateDetail(row.route_id)}
                  className="truncate text-[14px] font-semibold leading-[normal] text-black"
                >
                  {row.candidate_name}
                </Link>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            {row.is_registered == 0 ? (
              row.is_send == 1 ? (
                <span className="text-[12px]">Sent</span>
              ) : (
                <span className="text-[12px]">Pending</span>
              )
            ) : (
              <span></span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              is_auto_booked_on_apply={row.is_auto_booked_on_apply}
              extraEle={
                <>
                  {row.candidate_ref_no && row.candidate_ref_no !== "-" ? (
                    <span className="text-[12px] font-medium leading-[normal] text-txtColor">
                      {row.candidate_ref_no}
                    </span>
                  ) : null}
                </>
              }
            />
          </div>
        </div>
      ),
      width: "17.5%",
    },
    {
      name: "Contact",
      sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_email"),
      sortField: "candidate_email",
      selector: (row) => (
        <div className="flex flex-col">
          <BlurredText type="email">
            {row.mobile_phone_number && row.mobile_phone_number !== "-"
              ? getColumnNormalHtml(row.mobile_phone_number || "-")
              : null}
          </BlurredText>
          <BlurredText type="phone">
            <span className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor">
              {row.candidate_email}
            </span>
          </BlurredText>
        </div>
      ),
      width: "15%",
    },
    {
      name: "Address",
      sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "candidate_address"),
      sortField: "candidate_address",
      selector: (row) => (
        <div className="flex flex-col items-start">
          {row.miles && row.miles !== "-" ? <Miles miles={row.miles} /> : null}
          <BlurredText type="address">
            {getColumnNormalHtml(
              row.candidate_address,
              "line-clamp-2 whitespace-break-spaces truncate",
            )}
          </BlurredText>
        </div>
      ),
      width: "15%",
    },
    {
      name: "Role",
      sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "role_name"),
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          {row.license_number && row.license_number !== "-" ? (
            <span className="text-[12px] font-medium leading-[normal] text-txtColor">
              {row.license_number}
            </span>
          ) : null}
        </div>
      ),
      width: "9%",
    },
    {
      name: "Invited by",
      // sortable: true,
      // sortFunction: (rowA, rowB) => sortByText(rowA, rowB, "actioned_by"),
      // sortField: 'actioned_by',
      selector: (row) => (
        <div className="flex flex-col" data-tag="allowRowEvents">
          {getActionByHtml(row.actioned_by, row.actioned_at, "")}
        </div>
      ),
      width: "12%",
    },
    {
      name: "Date Invited",
      sortable: true,
      sortFunction: (rowA, rowB) =>
        sortByDate(
          moment(rowA.date_invited).format("DD/MM/YYYY"),
          moment(rowB.date_invited).format("DD/MM/YYYY"),
        ),
      sortField: "date_invited",

      selector: (row) => (
        <div className="flex flex-col gap-1">
          <div>
            {getColumnNormalHtml(moment(row.date_invited).format("DD/MM/YYYY"))}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {(row.profile_percent && row.profile_percent !== "-") ||
            row.is_registered ? (
              <span
                className={`leading-[ h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium ${getComplianceClass(
                  row.profile_percent,
                )}`}
              >
                {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
              </span>
            ) : null}
            {row.is_override_compliance_expiration_period_set &&
            isPremiumAccount &&
            user?.is_bank_client ? (
              <>
                <LiveTimerTooltip
                  actionedAt={""}
                  publisheddAt={moment(
                    row?.override_compliance_expiration_enddate,
                  ).format("DD/MM/YY 23:59")}
                  cls={"text-red"}
                  type={"invitedTimer"}
                />
              </>
            ) : null}
          </div>
        </div>
      ),
      width: "11%",
    },
    {
      name: "Date Reg",
      sortable: true,
      // sortFunction: (rowA, rowB) =>
      //   sortByDate(
      //     rowA.is_registered
      //       ? moment(rowA.date_registered).format("DD/MM/YYYY")
      //       : "-",
      //     rowB.is_registered
      //       ? moment(rowB.date_registered).format("DD/MM/YYYY")
      //       : "-",
      //   ),
      sortField: "date_registered",
      selector: (row) => (
        <div>
          {getColumnNormalHtml(
            row.is_registered
              ? moment(row.date_registered).format("DD/MM/YYYY")
              : "-",
          )}
        </div>
      ),
      width: "auto",
    },
    {
      name: "Total Hours",
      // sortable: true,
      // sortFunction: (rowA, rowB) => sortByNumber(rowA, rowB, "candidate_total_worked"),
      selector: (row) => (
        <div>
          {row.candidate_total_worked
            ? getColumnNormalHtml(row.candidate_total_worked)
            : 0}
        </div>
      ),
      width: "9%",
    },
  ];

  const dataTableRef = useRef();
  /**
   * Handles the table scroll event.
   */

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchInvitedCandidates({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  const handleSort = ({ sortField: newSortField }) => {
    const { sortByColumn, sortByOrder } = sorting; // get from Redux

    let newSortOrder = "asc";
    if (sortByColumn === newSortField) {
      newSortOrder = sortByOrder === "asc" ? "desc" : "asc";
    }

    dispatch(
      updateSorting({ sortByColumn: newSortField, sortByOrder: newSortOrder }),
    );
    dispatch(setInvitedCandidatesLoading(true));
    dispatch(deSelectAllCandidate());

    dispatch(
      updatePagination({
        ...pagination,
        currentPage: 1,
        isLastPage: false,
      }),
    );
    dispatch(fetchInvitedCandidates());
  };

  return (
    <div className="InvitedList_Wrapper mb-[58px]">
      <div className="flex h-full flex-col rounded-[10px] bg-white p-4 shadow-[0px_x1px_6px_0px_#E7EFFB]">
        <div className="mb-[20px] flex flex-col flex-wrap gap-3 border-b border-b-[#E8E8E8] pb-[11px] md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap items-center gap-2 md:gap-[21px]">
            <h4 className="main-heading tour-invited-heading font-semibold leading-[normal] text-black">
              {user.is_bank_client ? "Bank" : null} Candidates Invite & Manage
            </h4>
            <Actions />
            <SelectedCountText
              selectedCount={selectedCandidate.length}
              totalCount={totalCount}
              isSelectedAll={isSelectedAll}
              clearSelected={() => {
                dispatch(updateToggleSelected());
              }}
              handleSelectAll={(val) => {
                dispatch(toggleSelectedAll(val));
              }}
              updateSelected={(val) => {
                dispatch(updateSelectedCandidate(val));
              }}
              currentDataLength={data.length}
            />
          </div>
          <div className="flex flex-wrap gap-[15px]">
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_upload_candidates_resumes !==
              0 ? (
              <BulkInvite />
            ) : null}
            {!clientUserAllPermissions ||
            clientUserAllPermissions.is_allow_to_create_candidates !== 0 ? (
              <Link
                className="transition-linear tour-invited-button h-[40px] rounded-lg border border-primary bg-lightblue px-4 font-semibold leading-10 !text-primary hover:bg-primary hover:!text-white focus:outline-none"
                to={routingConstants.candidateCreate}
              >
                Create
              </Link>
            ) : null}

            <SearchFilter />
            <FilterModal />
          </div>
        </div>
        <div className="DataTable_Wrapper" ref={dataTableRef}>
          <DataTable
            columns={columns}
            data={data}
            onSort={handleSort}
            // persistTableHead={true}
            fixedHeader
            fixedHeaderScrollHeight="600px"
            progressPending={isLoading}
            noDataComponent={<TableNoContent />}
            clearSelectedRows={toggleSelected}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            onSelectedRowsChange={(state) => {
              let selectedCandidate = state.selectedRows;

              // let selectedEmails = state.selectedRows.map((item) => {
              //   return item.candidate_email;
              // });
              // let selectedIndex = state.selectedRows.map((item) => {
              //   return item.index;
              // });
              // let selectedCandidateId = state.selectedRows.map((item) => {
              //   return item.candidate_invitation_id;
              // });
              dispatch(updateSelectedCandidate(selectedCandidate));
              // dispatch(updateSelectedIndex(selectedIndex));
              // dispatch(updateSelectedCandidateIds(selectedCandidateId));
            }}
            className="_DataTable"
          />
        </div>
        {!isLoading && isPaginationLoading ? (
          <DataTablePaginationLoader />
        ) : null}
      </div>
    </div>
  );
};
